import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { useToast } from "@/shared/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import { useState, useEffect } from "react";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { useSuperAdminOrg } from "@/shared/contexts/SuperAdminOrgContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { OrganizationForm } from "./OrganizationForm";
import { OrganizationService } from "@/features/Organization/services/organizationService";
import { Loader2 } from "lucide-react";

// Valida se o valor é uma URL absoluta com protocolo http/https.
const isValidUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

export const OrganizationSettings = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const { selectedOrganization } = useSuperAdminOrg();
  const queryClient = useQueryClient();
  const [orgData, setOrgData] = useState({
    name: "",
    website: "",
    info: "",
    logo_url: null as string | null,
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);

  // Organização ativa da sessão; quem não tem uma selecionada cai no perfil.
  const activeOrgId = selectedOrganization?.id;

  // Fetch user's organization data with retry logic and better error handling
  const { data: organizationData, isLoading: isLoadingOrg, error: orgError, refetch: refetchOrg } = useQuery({
    queryKey: ['organization', activeOrgId ?? user?.id],
    queryFn: async () => {
      if (!user || !user.id) {
        console.error("[OrganizationSettings] Tentativa de buscar dados sem usuário autenticado");
        throw new Error("Usuário não autenticado");
      }

      try {
        let orgId: string | undefined = activeOrgId;

        if (!orgId) {
          // Sem organização ativa: cai na organização do perfil
          let profile;
          try {
            profile = await OrganizationService.getCurrentUserProfile();
          } catch (profileError) {
            await new Promise(resolve => setTimeout(resolve, 1000));
            profile = await OrganizationService.getCurrentUserProfile();
          }
          orgId = profile?.organization_id;
        }

        if (!orgId) {
          toast({
            title: t("settings.organization.noProfile"),
            description: t("settings.organization.noProfileDescription"),
            variant: "default"
          });
          return null;
        }

        // Buscar detalhes da organização usando o serviço
        const organizationDetails = await OrganizationService.getOrganizationDetails(orgId);
        
        return organizationDetails;
      } catch (error) {
        console.error('[OrganizationSettings] Erro ao buscar dados da organização:', error);
        
        // Exibir mensagem de erro para o usuário
        toast({
          title: t("common.error"),
          description: resolveApiErrorMessage(error, t),
          variant: "destructive"
        });
        
        throw error;
      }
    },
    enabled: !!user?.id,
    retry: 2,
    retryDelay: 1000
  });

  // Update organization data when it's loaded
  useEffect(() => {
    if (organizationData) {
      setOrgData({
        name: organizationData.name || "",
        website: organizationData.website || "",
        info: organizationData.info || "",
        logo_url: organizationData.logo_url || null,
      });
    }
  }, [organizationData]);

  // Update organization mutation
  const updateOrganization = useMutation({
    mutationFn: async () => {
      if (!user || !user.id) throw new Error("No user found");

      try {
        // Determina o org ID: organização ativa; sem ela, a do perfil
        let orgId: string | undefined = activeOrgId;
        if (!orgId) {
          const profile = await OrganizationService.getCurrentUserProfile();
          orgId = profile?.organization_id;
        }

        if (!orgId) {
          throw new Error('Organização não encontrada');
        }

        // Preparar dados da organização para atualização
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const organizationData: any = {
          name: orgData.name,
          website: orgData.website,
          info: orgData.info,
          logo_url: orgData.logo_url
        };

        // Adicionar o arquivo de logo se existir
        if (logoFile) {
          organizationData.logo = logoFile;
        }

        // Atualizar a organização usando o serviço
        const updatedOrganization = await OrganizationService.updateOrganization(
          orgId,
          organizationData
        );

        return updatedOrganization;
      } catch (error) {
        console.error('Erro na atualização da organização:', error);
        throw error;
      }
    },
    onSuccess: () => {
      // Limpar o arquivo após sucesso
      setLogoFile(null);
      
      toast({
        title: t("settings.organization.updateSuccess"),
        description: t("settings.organization.updateSuccessDescription"),
      });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
    },
    onError: (error) => {
      console.error('Error updating organization:', error);
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: t("settings.organization.updateError"),
      });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Nome é obrigatório: bloquear o salvamento e exibir um toast de validação,
    // evitando o feedback falso de sucesso quando o nome está vazio.
    if (!orgData.name.trim()) {
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: t("settings.organization.nameRequired"),
      });
      return;
    }

    // Website, quando preenchido, precisa ser uma URL válida (http/https).
    const website = orgData.website.trim();
    if (website && !isValidUrl(website)) {
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: t("settings.organization.websiteInvalid"),
      });
      return;
    }

    updateOrganization.mutate();
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{t("settings.organization.title")}</CardTitle>
        <CardDescription>
          {t("settings.organization.description")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoadingOrg ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin mr-2">
              <Loader2 className="h-8 w-8" />
            </div>
          </div>
        ) : orgError ? (
          <div className="flex flex-col items-center py-8 space-y-4">
            <div className="text-red-500 mb-4">
              <p className="text-center mb-2">{t("settings.organization.loadError")}</p>
              <p className="text-center text-sm">{orgError instanceof Error ? orgError.message : t("settings.organization.unknownError")}</p>
            </div>
            <button 
              onClick={() => refetchOrg()} 
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              {t("common.actions.retry")}
            </button>
          </div>
        ) : (
          <OrganizationForm
            orgData={orgData}
            onChange={(field, value, file) => {
              // Se recebeu um arquivo, armazená-lo
              if (file) {
                setLogoFile(file);
              }

              setOrgData(prev => {
                const newState = {
                  ...prev,
                  [field]: value
                };
                return newState;
              });
            }}
            onSubmit={handleSubmit}
            isSubmitting={updateOrganization.isPending}
            organizationId={organizationData?.id}
          />
        )}
      </CardContent>
    </Card>
  );
};
